import { useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { useSentryClient } from '../../services/sentry';
import type { NavErrorBoundaryConfig } from '../../ui/types';

export function useNavBoundaryContext({
	location = 'unknown',
	disableSentry = false,
	disableAnalytics = false,
	sentryConfig,
	onError,
}: NavErrorBoundaryConfig = {}): NavBoundaryContextType {
	const { fireEvent } = useAnalyticsEvents();

	const { sentryInitialised, sentryClient } = useSentryClient(
		disableSentry,
		sentryConfig,
		location,
	);

	const handleError = useCallback(
		(error: Error) => {
			// Send events to sentry
			if (sentryInitialised && sentryClient && fg('navx-1574-enable-sentry-logs')) {
				try {
					sentryClient.captureException(error, {
						data: {
							location,
						},
					});
				} catch (error) {
					// Log failure to send to Sentry
					fireEvent('operational.logToSentry.failed', {
						location,
						reason: error instanceof Error ? error.message : 'unknown',
					});
				}
			}

			// Fire Analytics event
			if (!disableAnalytics) {
				fireEvent('operational.errorBoundary.unhandledErrorCaught', {
					location,
					errorMessage: error.message,
					errorStack: error.stack || 'no stack trace',
				});
			}

			// handler Callback
			onError?.(error);
		},
		[sentryInitialised, sentryClient, disableAnalytics, onError, location, fireEvent],
	);

	return { handleError };
}

export type NavBoundaryContextType = {
	handleError: (error: Error, errorInfo?: React.ErrorInfo) => void;
};
